import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addToNetwork,
  removeFromNetwork,
  getMyNetwork,
  checkConnection,
  getNetworkStats,
  NetworkConnection,
  NetworkStats,
  CheckConnectionResponse,
} from '../api/networkApi';

const QUERY_KEYS = {
  network: ['network'],
  networkStats: ['network', 'stats'],
  connection: (targetUserId: string) => ['network', 'connection', targetUserId],
};

/**
 * Hook to fetch the user's network connections
 */
export const useNetwork = () => {
  return useQuery<NetworkConnection[], Error>({
    queryKey: QUERY_KEYS.network,
    queryFn: getMyNetwork,
  });
};

/**
 * Hook to fetch network statistics
 */
export const useNetworkStats = () => {
  return useQuery<NetworkStats, Error>({
    queryKey: QUERY_KEYS.networkStats,
    queryFn: getNetworkStats,
  });
};

/**
 * Hook to check if a user is in the network
 * @param targetUserId - The user ID to check connection with
 * @param enabled - Optional flag to enable/disable the query (default: true if targetUserId exists)
 */
export const useCheckConnection = (targetUserId: string, enabled?: boolean) => {
  return useQuery<CheckConnectionResponse, Error>({
    queryKey: QUERY_KEYS.connection(targetUserId),
    queryFn: () => checkConnection(targetUserId),
    enabled: enabled !== undefined ? enabled && !!targetUserId : !!targetUserId,
  });
};

/**
 * Hook to add a provider to the network
 */
export const useAddToNetwork = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addToNetwork,
    onSuccess: () => {
      // Invalidate and refetch network queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.network });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.networkStats });
      // Invalidate FA dashboard network providers query
      queryClient.invalidateQueries({ queryKey: ['advisor', 'network', 'providers'] });
    },
  });
};

/**
 * Hook to remove a provider from the network
 */
export const useRemoveFromNetwork = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeFromNetwork,
    onSuccess: () => {
      // Invalidate and refetch network queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.network });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.networkStats });
      // Invalidate FA dashboard network providers query
      queryClient.invalidateQueries({ queryKey: ['advisor', 'network', 'providers'] });
    },
  });
};

